import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Home, Users, FileText, Settings as SettingsIcon, Wifi, WifiOff, RefreshCw, Download, MapPin, Building2, ShoppingCart } from 'lucide-react';
import { useOnlineStatus, usePWAInstall } from '@/hooks/usePWA';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout({ children, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnline = useOnlineStatus();
  const { isInstallable, installPWA } = usePWAInstall();
  const { pendingCount, isSyncing, triggerSync } = useSyncStatus();
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Metaxon AG',
    logo_data: null,
    show_watermark: false
  });
  const [featureSettings, setFeatureSettings] = useState({
    orders_enabled: true
  });

  useEffect(() => {
    fetchCompanySettings();
    fetchFeatureSettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Firmeneinstellungen:', error);
    }
  };

  const fetchFeatureSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/settings/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureSettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Feature-Einstellungen:', error);
    }
  };

  const handleInstallPWA = async () => {
    const installed = await installPWA();
    if (installed) {
      toast.success('App erfolgreich installiert!');
    }
  };

  const handleManualSync = async () => {
    try {
      await triggerSync();
      toast.success('Synchronisierung erfolgreich!');
    } catch (error) {
      toast.error('Synchronisierung fehlgeschlagen');
    }
  };

  const allNavItems = [
    { path: '/', label: 'Dashboard', icon: Home, testId: 'nav-dashboard', permission: null },
    { path: '/customers', label: 'Kunden', icon: Users, testId: 'nav-customers', permission: 'customers' },
    { path: '/suppliers', label: 'Lieferanten', icon: Building2, testId: 'nav-suppliers', requireFeature: 'orders_enabled', permission: 'suppliers' },
    { path: '/delivery-notes', label: 'Lieferscheine', icon: FileText, testId: 'nav-delivery-notes', permission: 'delivery_notes' },
    { path: '/orders', label: 'Bestellungen', icon: ShoppingCart, testId: 'nav-orders', requireFeature: 'orders_enabled', permission: 'orders' },
    { path: '/tours', label: 'Touren', icon: MapPin, testId: 'nav-tours', permission: 'tours' },
    { path: '/settings', label: 'Einstellungen', icon: SettingsIcon, testId: 'nav-settings', permission: 'settings' }
  ];

  // Filter navigation based on feature settings AND user permissions
  const navItems = allNavItems.filter(item => {
    // Check feature toggle first
    if (item.requireFeature && !featureSettings[item.requireFeature]) {
      return false;
    }
    
    // Check user permissions (admins have access to everything)
    if (item.permission) {
      if (user?.role === 'admin') {
        return true; // Admins see everything
      }
      return user?.permissions?.[item.permission] === true;
    }
    
    return true; // Items without permission requirement are always shown
  });

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ background: 'linear-gradient(135deg, #0A1F33 0%, #00B4D8 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              {companySettings.logo_data ? (
                <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg flex items-center justify-center bg-white bg-opacity-20 overflow-hidden">
                  <img 
                    src={companySettings.logo_data} 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg flex items-center justify-center bg-white bg-opacity-20">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {companySettings.company_name}
                </h1>
                <p className="text-xs text-white text-opacity-80">Lieferschein Service</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Online/Offline Status */}
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${isOnline ? 'bg-green-500 bg-opacity-20' : 'bg-red-500 bg-opacity-20'}`}>
                {isOnline ? (
                  <>
                    <Wifi className="h-4 w-4 text-green-300" />
                    <span className="text-xs text-green-200">Online</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="h-4 w-4 text-red-300" />
                    <span className="text-xs text-red-200">Offline</span>
                  </>
                )}
              </div>

              {/* Pending Sync Badge */}
              {pendingCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualSync}
                  disabled={isSyncing || !isOnline}
                  className="bg-orange-500 bg-opacity-20 border-orange-300 border-opacity-30 text-white hover:bg-orange-500 hover:bg-opacity-30 relative"
                  title={`${pendingCount} Unterschrift(en) warten auf Synchronisierung`}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                  <span className="text-xs">{pendingCount}</span>
                  {isOnline && (
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                    </span>
                  )}
                </Button>
              )}

              {/* Install PWA Button */}
              {isInstallable && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleInstallPWA}
                  className="bg-white bg-opacity-20 border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-30"
                >
                  <Download className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">App installieren</span>
                </Button>
              )}

              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-white">{user?.username}</p>
                <p className="text-xs text-white text-opacity-80">{user?.email || 'Benutzer'}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="bg-white bg-opacity-20 border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-30"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-testid={item.testId}
                  className={`flex items-center px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-blue-600 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative">
        {children}
        
        {/* Watermark Logo */}
        {companySettings.show_watermark && companySettings.logo_data && (
          <div className="fixed inset-0 pointer-events-none flex items-center justify-center z-0">
            <img 
              src={companySettings.logo_data}
              alt="Watermark"
              className="w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 opacity-5 object-contain"
            />
          </div>
        )}
      </main>
    </div>
  );
}
